// Makes a tenant-chosen brand color safe to use as a theme's primary/secondary color in a given
// color scheme, without losing the tenant's actual brand identity. Only lightness is remapped
// (clamped into a safe band per light/dark mode, then nudged until the color is legible on the
// page) — hue and saturation are left untouched, so the color stays recognizably "theirs." A
// tenant can only ever pick one color, but light and dark mode need very different lightness
// ranges to stay legible against their opposite backgrounds (see theme.tsx's own hardcoded
// defaults: a medium blue for light mode vs. a much lighter pastel blue for dark mode) — this is
// what lets one chosen color work safely in both.

function hexToRgb(hex: string): [number, number, number] {
    const normalized = hex.replace('#', '');
    const full = normalized.length === 3
        ? normalized.split('').map((c) => c + c).join('')
        : normalized;
    const num = parseInt(full, 16);
    return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
}

function rgbToHex(r: number, g: number, b: number): string {
    return '#' + [r, g, b]
        .map((c) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, '0'))
        .join('');
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
    const rn = r / 255;
    const gn = g / 255;
    const bn = b / 255;
    const max = Math.max(rn, gn, bn);
    const min = Math.min(rn, gn, bn);
    const l = (max + min) / 2;
    const d = max - min;

    if (d === 0) {
        return [0, 0, l];
    }

    const s = d / (1 - Math.abs(2 * l - 1));
    let h: number;
    switch (max) {
        case rn:
            h = ((gn - bn) / d) % 6;
            break;
        case gn:
            h = (bn - rn) / d + 2;
            break;
        default:
            h = (rn - gn) / d + 4;
            break;
    }
    h *= 60;
    if (h < 0) {
        h += 360;
    }

    return [h, s, l];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;

    let r = 0;
    let g = 0;
    let b = 0;
    if (h < 60) { [r, g, b] = [c, x, 0]; }
    else if (h < 120) { [r, g, b] = [x, c, 0]; }
    else if (h < 180) { [r, g, b] = [0, c, x]; }
    else if (h < 240) { [r, g, b] = [0, x, c]; }
    else if (h < 300) { [r, g, b] = [x, 0, c]; }
    else { [r, g, b] = [c, 0, x]; }

    return [(r + m) * 255, (g + m) * 255, (b + m) * 255];
}

// WCAG relative luminance — the standard formula, not an approximation.
function getRelativeLuminance(r: number, g: number, b: number): number {
    const [rl, gl, bl] = [r, g, b].map((c) => {
        const s = c / 255;
        return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rl + 0.7152 * gl + 0.0722 * bl;
}

function getLuminanceOfHex(hex: string): number {
    const [r, g, b] = hexToRgb(hex);
    return getRelativeLuminance(r, g, b);
}

// WCAG contrast ratio between two relative luminances, in either order.
function getContrastRatio(luminanceA: number, luminanceB: number): number {
    return (Math.max(luminanceA, luminanceB) + 0.05) / (Math.min(luminanceA, luminanceB) + 0.05);
}

const WHITE_TEXT = '#ffffff';
const DARK_TEXT = '#0D1B2A';

// Picks whichever of the two text colors has the higher contrast against the background. Compared
// directly rather than against a luminance cut-off: the well-known 0.179 crossover is the
// white-vs-*black* break-even, and DARK_TEXT is not black (its true break-even is ~0.20), so a
// fixed threshold picked the lower-contrast text for colors in between (e.g. #cc33a6).
function getContrastText(hex: string): string {
    const luminance = getLuminanceOfHex(hex);
    const withWhite = getContrastRatio(luminance, getLuminanceOfHex(WHITE_TEXT));
    const withDark = getContrastRatio(luminance, getLuminanceOfHex(DARK_TEXT));

    return withWhite >= withDark ? WHITE_TEXT : DARK_TEXT;
}

// The page and card backgrounds theme.tsx gives each color scheme (theme.test.ts checks they still
// match). The brand color is used as TEXT and icon color on these — nav titles, headlines, links —
// so it has to stay legible against both.
export const PAGE_BACKGROUNDS: Record<'light' | 'dark', readonly [string, string]> = {
    light: ['#f5f7fa', '#ffffff'],
    dark: ['#121212', '#1E1E2F'],
};

// WCAG's minimum for large text and UI components is 3:1; the extra 0.05 is margin so that rounding
// to 8-bit hex can never land a color just under it.
const MIN_CONTRAST_ON_PAGE = 3.05;

// Light mode: clamped to a medium band, dark enough to read against light/white backgrounds but
// not so dark it looks muddy. Dark mode: clamped to a bright band, matching the app's own
// hardcoded default (#90CAF9-equivalent lightness) so it reads clearly against near-black
// backgrounds (#121212/#1E1E2F).
const LIGHTNESS_BAND: Record<'light' | 'dark', [number, number]> = {
    light: [0.35, 0.55],
    dark: [0.6, 0.8],
};

const LIGHTNESS_STEP = 0.01;

function hslToHex(h: number, s: number, l: number): string {
    const [r, g, b] = hslToRgb(h, s, l);
    return rgbToHex(r, g, b);
}

function isLegibleOnPage(hex: string, mode: 'light' | 'dark'): boolean {
    const luminance = getLuminanceOfHex(hex);
    return PAGE_BACKGROUNDS[mode].every((background) => getContrastRatio(luminance, getLuminanceOfHex(background)) >= MIN_CONTRAST_ON_PAGE);
}

export function makeAccessibleColor(hex: string, mode: 'light' | 'dark'): { main: string; contrastText: string } {
    const [r, g, b] = hexToRgb(hex);
    const [h, s, l] = rgbToHsl(r, g, b);
    const [minL, maxL] = LIGHTNESS_BAND[mode];

    let lightness = Math.min(maxL, Math.max(minL, l));
    let main = hslToHex(h, s, lightness);

    // The band is only a starting point. Perceived luminance varies enormously by hue at the same HSL
    // lightness — pure yellow at 0.5 is nearly white (1.1:1 on the white page), pure blue at 0.6 is
    // dim on the near-black one (2.7:1) — so keep moving away from the page background, in 1% steps,
    // until the color is legible on it. Hue and saturation never change.
    const direction = mode === 'light' ? -1 : 1;
    for (let step = 0; step < 1 / LIGHTNESS_STEP && !isLegibleOnPage(main, mode); step++) {
        lightness = Math.min(1, Math.max(0, lightness + direction * LIGHTNESS_STEP));
        main = hslToHex(h, s, lightness);
    }

    return { main, contrastText: getContrastText(main) };
}
